import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, ChevronDown, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const Navigation = ({ isScrolled }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);

    const handleDropdownToggle = (index) => {
        setActiveDropdown(activeDropdown === index ? null : index);
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
        setActiveDropdown(null);
    };

    const navItems = [
        { label: 'Home', link: '/' },
        {
            label: 'About',
            link: '/about',
        },
        {
            label: 'Practice Areas',
            link: '/practice-areas',
            dropdown: [
                { label: 'Criminal Law', link: '/services/criminal-law' },
                { label: 'Family Law', link: '/services/family-law' },
                { label: 'Civil Law', link: '/services/civil-law' },
                { label: 'Business Law', link: '/services/business-law' },
                { label: 'Excise and Taxation', link: '/services/labor-law' },
            ]
        },
        {
            label: 'Services',
            link: '/services',
        },
        { label: 'FAQs', link: '/faqs' },
        { label: 'Contact', link: '/contact' },
    ];

    return (
        <nav className={`py-4 transition-all duration-300 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
            <div className="flex justify-between items-center">
                {/* Logo */}
                <Link to="/" className="font-bold text-2xl flex items-center">
                    <span className={`transition-colors ${isScrolled ? 'text-indigo-600' : 'text-white'}`}>
                        Wisdoms Law
                    </span>
                </Link>

                {/* Desktop Navigation */}
                <div className="hidden lg:flex items-center space-x-8">
                    {navItems.map((item, index) => (
                        <div key={index} className="relative group">
                            {item.dropdown ? (
                                <>
                                    <button
                                        className="flex items-center space-x-1 group"
                                        onClick={() => handleDropdownToggle(index)}
                                    >
                                        <span className="group-hover:text-indigo-500 transition-colors">{item.label}</span>
                                        <ChevronDown className="w-4 h-4 group-hover:text-indigo-500 transition-colors" />
                                    </button>
                                    {activeDropdown === index && (
                                        <div className={`absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg overflow-hidden z-50 transition-all duration-200 text-gray-800`}>
                                            {item.dropdown.map((dropdownItem, dropdownIndex) => (
                                                <Link
                                                    key={dropdownIndex}
                                                    to={dropdownItem.link}
                                                    className="block px-4 py-3 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                                                    onClick={closeMenu}
                                                >
                                                    {dropdownItem.label}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <Link
                                    to={item.link}
                                    className="hover:text-indigo-500 transition-colors"
                                >
                                    {item.label}
                                </Link>
                            )}
                        </div>
                    ))}
                </div>

                {/* Free Inquiry Button */}
                <div className="hidden lg:block">
                    <button
                        onClick={() => {
                            const phoneNumber = '923329754400';
                            const message = encodeURIComponent('I am here for free consultation');
                            const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
                            window.open(whatsappUrl, '_blank');
                        }}
                        className={`px-5 py-2 rounded-md font-medium transition-colors ${
                            isScrolled
                                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                                : 'bg-white text-indigo-700 hover:bg-gray-100'
                        }`}
                    >
                        Free Inquiry
                    </button>
                </div>

                {/* Mobile Menu Button */}
                <button
                    className="lg:hidden focus:outline-none"
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                >
                    {isMenuOpen ? (
                        <X className="w-6 h-6" />
                    ) : (
                        <Menu className="w-6 h-6" />
                    )}
                </button>
            </div>

            {/* Mobile Menu */}
            {isMenuOpen && (
                <div className="lg:hidden mt-4 rounded-lg bg-white shadow-xl text-gray-800 overflow-hidden">
                    {navItems.map((item, index) => (
                        <div key={index} className="border-b border-gray-100 last:border-b-0">
                            {item.dropdown ? (
                                <div>
                                    <button
                                        className="flex items-center justify-between w-full px-4 py-3 hover:bg-gray-50"
                                        onClick={() => handleDropdownToggle(index)}
                                    >
                                        <span>{item.label}</span>
                                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === index ? 'transform rotate-180' : ''}`} />
                                    </button>
                                    {activeDropdown === index && (
                                        <div className="bg-gray-50 pl-8">
                                            {item.dropdown.map((dropdownItem, dropdownIndex) => (
                                                <Link
                                                    key={dropdownIndex}
                                                    to={dropdownItem.link}
                                                    className="block px-4 py-3 hover:bg-gray-100 border-t border-gray-100"
                                                    onClick={closeMenu}
                                                >
                                                    {dropdownItem.label}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Link
                                    to={item.link}
                                    className="block px-4 py-3 hover:bg-gray-50"
                                    onClick={closeMenu}
                                >
                                    {item.label}
                                </Link>
                            )}
                        </div>
                    ))}
                    <div className="px-4 py-4">
                        <button
                            onClick={() => {
                                const phoneNumber = '923329754400';
                                const message = encodeURIComponent('I am here for free consultation');
                                const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
                                window.open(whatsappUrl, '_blank');
                                closeMenu();
                            }}
                            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md font-medium hover:bg-indigo-700 transition-colors"
                        >
                            Free Inquiry
                        </button>
                    </div>
                </div>
            )}
        </nav>
    );
};

const Header = () => {
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Handle WhatsApp contact
    const handleWhatsappContact = () => {
        const phoneNumber = '923329754400';
        const message = encodeURIComponent('I am here for free consultation');
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
        window.open(whatsappUrl, '_blank');
    };

    return (
        <header className={`fixed w-full z-50 duration-300 ease-in-out ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
            <div className="container mx-auto px-4">
                <div className="hidden lg:flex justify-between items-center py-2 text-sm">
                    <div className="flex items-center space-x-6">
                        <button 
                            onClick={handleWhatsappContact}
                            className={`flex items-center transition-colors ${isScrolled ? 'text-gray-700 hover:text-indigo-600' : 'text-gray-200 hover:text-white'}`}
                        >
                            <Phone className="w-4 h-4 mr-2" />
                            +92 332-9754400
                        </button>
                        <a 
                            href="mailto:chkhurram379@gmail.com" 
                            className={`flex items-center transition-colors ${isScrolled ? 'text-gray-700 hover:text-indigo-600' : 'text-gray-200 hover:text-white'}`}
                        >
                            <Mail className="w-4 h-4 mr-2" />
                            chkhurram379@gmail.com
                        </a>
                    </div>
                    <div className="flex items-center">
                        <div 
                            className={`flex items-center transition-colors ${isScrolled ? 'text-gray-700' : 'text-gray-200'}`}
                        >
                            <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                            <span className="truncate">Office no 330 Sher Shah Suri block 2nd floor Dist Courts Rawalpindi</span>
                        </div>
                    </div>
                </div>
                <Navigation isScrolled={isScrolled} />
            </div>
        </header>
    );
};

export default Header;